import React, { useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
  useRowSelect,
  Column,
  Row as TableRow,
  CellProps,
  UseGlobalFiltersInstanceProps,
  UsePaginationInstanceProps,
  TableInstance,
} from "react-table";
import {
  Table,
  Row,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Input,
} from "reactstrap";

import {
  // Filter,
  DefaultColumnFilter,
} from "./Filters";

// search, date, status filter
import GlobalFilter from "../../components/Common/GlobalSearchFilter";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import NoDataFound from "../Common/NoDataFound";

const ROWS_PER_PAGE_OPTIONS = [5, 10, 15, 20];

// Define types for component props
interface PageParams {
  currentPage: number;
  rowsPerPage: number;
}

interface FilterParams {
  [key: string]: any;
}

interface SelectFilterOption {
  label: string;
  value: string | number;
}

interface SelectFilterOptionGroup {
  options: SelectFilterOption[];
}

/**
 * Extended Column type with better autocomplete support for common properties.
 * react-table's Column type uses index signatures which prevent autocomplete.
 * This interface explicitly defines commonly used properties while maintaining
 * full compatibility with react-table's Column type.
 */
export interface TableColumn<T extends object> {
  Header?: string | React.ReactNode | ((props: any) => React.ReactNode);
  accessor?: keyof T | string | ((row: T) => any);
  Cell?: (props: CellProps<T>) => React.ReactNode;
  filterable?: boolean;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  disableResizing?: boolean;
  disableSortBy?: boolean;
  disableFilters?: boolean;
  // Allow any other Column properties
  [key: string]: any;
}

interface TableContainerProps<T extends object> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  pageParams: PageParams;
  setPageParams: (params: Partial<PageParams>) => void;
  filterParams?: FilterParams;
  setFilterParams?: (params: FilterParams) => void;
  onRowClick?: (row: T) => void;
  disableRowModal?: boolean;
  datePicker?: boolean;
  selectFilterOptions?: (SelectFilterOption | SelectFilterOptionGroup)[];
  isGlobalSearch?: boolean;
  isGlobalFilter?: boolean;
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  divClass?: string;
}

const TableContainer = <T extends object>({
  columns,
  data,
  loading = false,
  pageCount,
  hasPreviousPage,
  hasNextPage,
  pageParams,
  setPageParams,
  filterParams: _filterParams,
  setFilterParams,
  onRowClick,
  disableRowModal = false,
  datePicker,
  selectFilterOptions,
  isGlobalSearch = false,
  isGlobalFilter = false,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
}: TableContainerProps<T>) => {
  const { t } = useTranslation();
  // built-in hooks from react-table library
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    setGlobalFilter,
    pageOptions,
  } = useTable<T>(
    {
      columns: columns as readonly Column<T>[],
      data,
      defaultColumn: { Filter: DefaultColumnFilter } as any,
      initialState: {
        pageSize: pageParams.rowsPerPage,
        pageIndex: pageParams.currentPage,
      } as any,
      pageCount: pageCount,
      manualPagination: true,
    } as any,
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  ) as TableInstance<T> &
    UseGlobalFiltersInstanceProps<T> &
    UsePaginationInstanceProps<T> & {
      page: Array<TableRow<T>>;
      pageOptions: number[];
    };

  const generateSortingIndicator = (column: any): string => {
    return column.isSorted ? (column.isSortedDesc ? " " : "") : "";
  };

  // handle rows per page & pagination, update pageParams state
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const newRowsPerPage = parseInt(event.target.value);
    setPageParams({
      rowsPerPage: newRowsPerPage,
      currentPage: 1, // Reset to first page when changing rows per page
    });
  };

  const handleCurrentPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setPageParams({
      currentPage: event.target.value ? parseInt(event.target.value) : 1,
    });
  };

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string | null>(null);

  const handleRowClick = (row: TableRow<T>): void => {
    if (disableRowModal && onRowClick) {
      // If modal is disabled and onRowClick is provided, use the custom handler
      onRowClick(row.original);
      return;
    }
    if (disableRowModal) return;
    // Otherwise, open the modal
    setModalContent(row.id);
    setIsModalOpen(true);
  };

  const toggleModal = (): void => {
    setIsModalOpen(!isModalOpen);
  };

  const handlePrevPage = (): void => {
    setPageParams({
      currentPage: pageParams.currentPage - 1,
    });
  };

  const handleNextPage = (): void => {
    setPageParams({
      currentPage: pageParams.currentPage + 1,
    });
  };

  return (
    <Fragment>
      {isGlobalFilter && (
        <Row className="mb-3">
          {/* GlobalFilter is search / date range / select dropdown  */}
          <GlobalFilter
            globalFilter={(state as any).globalFilter}
            setGlobalFilter={setGlobalFilter}
            datePicker={datePicker}
            selectFilterOptions={selectFilterOptions}
            setFilterParams={setFilterParams}
            filterParams={_filterParams}
          />
        </Row>
      )}

      <div className={`${divClass ? divClass : ""} overflow-x-auto`}>
        <Table {...getTableProps()} className={tableClass}>
          <thead className={theadClass}>
            {headerGroups.map((headerGroup) => (
              <tr
                className={trClass}
                key={headerGroup.id}
                {...headerGroup.getHeaderGroupProps()}
              >
                {headerGroup.headers.map((column: any) => {
                  const columnStyles: React.CSSProperties = {};
                  if (column.width) columnStyles.width = column.width;
                  if (column.minWidth) columnStyles.minWidth = column.minWidth;
                  if (column.maxWidth) columnStyles.maxWidth = column.maxWidth;

                  return (
                    <th
                      key={column.id}
                      className={thClass}
                      style={columnStyles}
                      {...(column.getSortByToggleProps
                        ? column.getSortByToggleProps()
                        : {})}
                    >
                      {column.render("Header")}
                      {generateSortingIndicator(column)}
                      {/* <Filter column={column} /> */}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length > 0 &&
              page.map((row: TableRow<T>) => {
                prepareRow(row);
                return (
                  <Fragment key={row.getRowProps().key}>
                    <tr
                      className={
                        disableRowModal && onRowClick
                          ? "clickable-row"
                          : disableRowModal
                          ? "no-hover"
                          : ""
                      }
                      onClick={() => handleRowClick(row)}
                      style={
                        disableRowModal && onRowClick
                          ? { cursor: "pointer" }
                          : undefined
                      }
                    >
                      {row.cells.map((cell) => {
                        const column = cell.column as any;
                        const cellStyles: React.CSSProperties = {};
                        if (column.width) cellStyles.width = column.width;
                        if (column.minWidth)
                          cellStyles.minWidth = column.minWidth;
                        if (column.maxWidth)
                          cellStyles.maxWidth = column.maxWidth;

                        const cellProps = cell.getCellProps();
                        return (
                          <td
                            key={cellProps.key}
                            {...cellProps}
                            className="text-primary"
                            style={cellStyles}
                          >
                            {loading ? (
                              <Skeleton count={0.8} />
                            ) : (
                              cell.render("Cell")
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </Fragment>
                );
              })}
            {page.length === 0 &&
              loading &&
              Array.from({ length: 10 }, (_, index) => index).map((row) => {
                return (
                  <Fragment key={row}>
                    <tr>
                      {Array.from(
                        { length: headerGroups[0].headers.length },
                        (_, index) => index
                      ).map((cell) => {
                        return (
                          <td key={cell}>
                            <Skeleton count={0.8} />
                          </td>
                        );
                      })}
                    </tr>
                  </Fragment>
                );
              })}
          </tbody>
        </Table>
        {/* If No Data Found */}
        {page.length === 0 && !loading && <NoDataFound />}
        {!disableRowModal && (
          <Modal isOpen={isModalOpen} toggle={toggleModal} centered={true}>
            <ModalHeader toggle={toggleModal}>SMS Subject</ModalHeader>
            <ModalBody>
              {modalContent &&
                page.map(
                  (row: TableRow<T>) =>
                    row.id === modalContent && (
                      <div key={row.id} className="sms-modal-content">
                        <div className="sms-modal-content__left">
                          <h5 className="short">
                            SMS ID: <span>{(row.original as any).id}</span>
                          </h5>
                          <h5 className="short">
                            Workflow ID:{" "}
                            <span>{(row.original as any).workflowId}</span>
                          </h5>
                          <h5 className="short">
                            Workflow:{" "}
                            <span>{(row.original as any).workflowTitle}</span>
                          </h5>

                          <h5 className="sms-modal-content__message">
                            Message Body:{" "}
                            <div>{(row.original as any).workflowMessage}</div>
                          </h5>
                        </div>
                        <div className="sms-modal-content__right">
                          <h5>
                            Phone Number:{" "}
                            <span>{(row.original as any).phoneNumber}</span>
                          </h5>
                          <h5>
                            Recipient's Name:{" "}
                            <span>
                              {(row.original as any).recipientsFirstName}{" "}
                              {(row.original as any).recipientsLastName}
                            </span>
                          </h5>
                          <h5>
                            Sending Time:{" "}
                            <span>{(row.original as any).sentAt}</span>
                          </h5>
                          {/* Parse sentResult JSON and display it nicely */}
                          {/* Parse sentResult JSON and display select fields */}
                          {(() => {
                            try {
                              const result = JSON.parse(
                                (row.original as any).sentResult
                              );
                              const parts = result.data.split(",");

                              return (
                                <>
                                  <h5>
                                    Sent Result Cost: <span>{parts[0]}</span>
                                  </h5>
                                  <h5>
                                    Messages Sent: <span>{parts[1]}</span>
                                  </h5>
                                  <h5>
                                    Message ID: <span>{parts[4]}</span>
                                  </h5>

                                  {/* Use statusCode and state instead of Delivered/Failed */}
                                  <h5>
                                    Status Code: <span>{result.status}</span>
                                  </h5>
                                  <h5 className="sms-modal-content__status">
                                    Sending Result:{" "}
                                    <span
                                      className={
                                        (row.original as any).state ===
                                          "SUCCESS" ||
                                        (row.original as any).state ===
                                          "COMPLETED"
                                          ? "bg-success"
                                          : "bg-danger"
                                      }
                                    >
                                      {(row.original as any).state ===
                                        "SUCCESS" ||
                                      (row.original as any).state ===
                                        "COMPLETED"
                                        ? "Successful"
                                        : "Failed"}
                                    </span>
                                  </h5>
                                </>
                              );
                            } catch (e) {
                              return (
                                <h5>
                                  Sent Result:{" "}
                                  <span>
                                    {(row.original as any).sentResult}
                                  </span>
                                </h5>
                              );
                            }
                          })()}
                          {(row.original as any).relatedSms && (
                            <h5 className="sms-modal-content__related-sms">
                              Related SMS:
                              <div>
                                <ul>
                                  {(row.original as any).relatedSms.map(
                                    (item: any) => (
                                      <li key={item.id}>{item.subject}</li>
                                    )
                                  )}
                                </ul>
                              </div>
                            </h5>
                          )}
                        </div>
                      </div>
                    )
                )}
            </ModalBody>
          </Modal>
        )}
      </div>

      {/* +++++++++ ROWS PER PAGE ++++++++++++ */}
      {page.length > 0 && (
        <div className={`d-flex ${isGlobalSearch ? "pt-3" : ""}`}>
          {/* Hidden when it changes nothing: one page that also fits the
              smallest size. Stays visible after choosing a larger size. */}
          {isGlobalSearch &&
            (pageCount > 1 ||
              hasPreviousPage ||
              page.length > ROWS_PER_PAGE_OPTIONS[0]) && (
            <Col className="d-flex align-items-center gap-2 justify-content-center">
              <label className="fw-semibold text-primary mb-0">
                {t("table.rowsPerPage")}
              </label>
              <select
                className="form-select w-auto"
                value={pageParams.rowsPerPage}
                onChange={(e) => handleRowsPerPageChange(e)}
              >
                {ROWS_PER_PAGE_OPTIONS.map((size, idx) => (
                  <option key={idx} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </Col>
          )}

          {/* +++++++++ WIP PAGINATION ++++++++++++ */}
          {pageCount > 1 && (
            <Row className="justify-content-md-end justify-content-center align-items-center p-2">
              <Col className="col-md-auto">
                <button
                  type="button"
                  className="table-pagination-btn"
                  onClick={() => handlePrevPage()}
                  style={{ display: !hasPreviousPage ? "none" : undefined }}
                  disabled={!hasPreviousPage}
                >
                  <i className="ri-arrow-left-line"></i>
                  {t("table.prev")}
                </button>
              </Col>
              <Col className="col-md-auto px-0">
                <Input
                  type="select"
                  // min={1}
                  // max={10}
                  //defaultValue={pageParams.currentPage}
                  value={pageParams.currentPage}
                  onChange={(e) => handleCurrentPageChange(e as any)}
                >
                  {/* TODO - get total of pages from backend */}
                  {pageOptions &&
                    pageOptions.map((n) => (
                      <option value={n + 1} key={n + 1}>
                        {n + 1}
                      </option>
                    ))}
                </Input>
              </Col>

              <Col className="col-md-auto">
                <button
                  type="button"
                  className="table-pagination-btn"
                  style={{ display: !hasNextPage ? "none" : undefined }}
                  onClick={() => handleNextPage()}
                  disabled={!hasNextPage}
                >
                  {t("table.next")}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </Col>
            </Row>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default TableContainer;
export type {
  TableContainerProps,
  PageParams,
  FilterParams,
  SelectFilterOption,
  SelectFilterOptionGroup,
};
